import styled, { keyframes } from 'styled-components'

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  animation: ${fadeIn} 0.2s ease-out;
`

const Panel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  max-width: 90vw;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.3s ease-out;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
`

const PanelTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.text};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 0;
  line-height: 1;
  transition: color ${({ theme }) => theme.transitions.normal};

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};
`

const PersonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const PersonCard = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    ${({ theme, $clickable }) => $clickable ? `
      border-color: ${theme.colors.primary};
      background: white;
      transform: translateX(4px);
    ` : ''}
  }
`

const PersonAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const PersonInfo = styled.div`
  flex: 1;
`

const PersonName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`

const PersonRole = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`

interface LoadIndicatorProps {
    load: number
}

const LoadIndicator = styled.div<LoadIndicatorProps>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  background: ${({ load, theme }) =>
        load > 100
            ? theme.colors.danger
            : load >= 80
                ? theme.colors.warning
                : theme.colors.success};
  color: white;
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textMuted};
`

interface Person {
    id: string
    name: string
    role?: string
    load?: number
}

interface PersonsPanelProps {
    isOpen: boolean
    title: string
    persons: Person[]
    description?: string
    showLoad?: boolean
    onClose: () => void
    onPersonClick?: (person: Person) => void
}

export const PersonsPanel: React.FC<PersonsPanelProps> = ({
    isOpen,
    title,
    persons,
    description,
    showLoad = false,
    onClose,
    onPersonClick,
}) => {
    if (!isOpen) return null

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2)
    }

    return (
        <>
            <Overlay onClick={onClose} />
            <Panel>
                <PanelHeader>
                    <PanelTitle>{title}</PanelTitle>
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </PanelHeader>
                <PanelContent>
                    {description && (
                        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                            {description}
                        </p>
                    )}
                    {persons.length === 0 ? (
                        <EmptyState>
                            <p>No hay personas en esta categoría</p>
                        </EmptyState>
                    ) : (
                        <PersonsList>
                            {persons.map((person) => (
                                <PersonCard
                                    key={person.id}
                                    $clickable={!!onPersonClick}
                                    onClick={() => onPersonClick?.(person)}
                                >
                                    <PersonAvatar>{getInitials(person.name)}</PersonAvatar>
                                    <PersonInfo>
                                        <PersonName>{person.name}</PersonName>
                                        {person.role && <PersonRole>{person.role}</PersonRole>}
                                    </PersonInfo>
                                    {showLoad && person.load !== undefined && (
                                        <LoadIndicator load={person.load}>
                                            {person.load}%
                                        </LoadIndicator>
                                    )}
                                </PersonCard>
                            ))}
                        </PersonsList>
                    )}
                </PanelContent>
            </Panel>
        </>
    )
}
